import {
  AccountingEventsGroup,
  CanonicalBusinessEvent,
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../types";
import { getPaymentMethod } from "./AccountingEventsUtils";
import { shouldHideAccountingFlow } from "./flowTypeUtils";
import { getDbdPaymentMethodName } from "./PaymentUtils";
import {
  isAccountingEventsGroup,
  isCanonicalBusinessEvent,
  isDigitalBusinessDocument,
  isDigitalCostDocument,
  isDigitalPaymentDocument,
} from "./typeChecks";

export const GL_MAPPING: Record<string, string> = {
  "318": "TVOD",
  "613": "Channels",
  "628": "SVOD",
  "739": "Device Batch Fees",
  "760": "Ad Revenue Costs",
  "796": "SWM",
  "806": "CMO",
  "807": "CMO",
  "808": "CMO",
  "809": "CMO",
  "810": "CMO",
  "811": "CMO",
  "816": "Ad Revenue Costs",
  "817": "Ad Revenue Costs",
  "819": "Ad Revenue Costs",
  "820": "Ad Revenue Costs",
};

export const getGlMapping = (gl: number | string): string => {
  if (typeof gl === "string" && gl.includes("(")) {
    return gl;
  }

  if (gl === undefined || gl === null) {
    return "-";
  }

  const glString = gl.toString();
  if (!GL_MAPPING[glString]) {
    return glString;
  }

  const glName = GL_MAPPING[glString];
  return `${glName} (${glString})`;
};

export type SummaryEvent =
  | AccountingEventsGroup
  | DigitalBusinessDocument
  | DigitalCostDocument
  | DigitalPaymentDocument;

export interface SummaryLastItem {
  accountingEventsGroup?: AccountingEventsGroup;
  digitalBusinessDocument?: DigitalBusinessDocument;
  digitalCostDocument?: DigitalCostDocument;
  digitalPaymentDocument?: DigitalPaymentDocument;
}

export interface SummaryOrderDetails {
  orderDate?: string;
  glProductGroup?: number;
  consumptionType?: string;
  fulfillmentType?: string;
  lastItem?: SummaryLastItem;
  paymentMethod?: string;
  isMgm?: boolean;
  isBundleImmediate?: boolean;
  isSeasonPassBundle?: boolean;
  isPreorderBundle?: boolean;
  countryCodeOverride?: string;
  declaredCountryCode?: string;
}

const EMPTY_ORDER_DETAILS: SummaryOrderDetails = {
  orderDate: undefined,
  glProductGroup: undefined,
  consumptionType: undefined,
  fulfillmentType: undefined,
  lastItem: undefined,
  paymentMethod: undefined,
  isMgm: undefined,
  isBundleImmediate: undefined,
  isSeasonPassBundle: undefined,
  isPreorderBundle: undefined,
  countryCodeOverride: undefined,
  declaredCountryCode: undefined,
};

function withOrderDetailsDefaults(
  overrides: Partial<SummaryOrderDetails>
): SummaryOrderDetails {
  return {
    ...EMPTY_ORDER_DETAILS,
    ...overrides,
  };
}

export function deriveOrderDetails(
  eventData: SummaryEvent[],
  cbeData: CanonicalBusinessEvent[] = []
): SummaryOrderDetails {
  if (!eventData || eventData.length === 0) {
    return withOrderDetailsDefaults({});
  }

  // Iterate in reverse to use latest versions first (as have the most data).
  for (let i = eventData.length - 1; i >= 0; i -= 1) {
    const event = eventData[i];

    if (isDigitalBusinessDocument(event)) {
      let dbdOrderDetails: SummaryOrderDetails | undefined;

      try {
        const firstProduct =
          event.body.body.products &&
          Object.values(event.body.body.products)[0];
        const paymentId = Object.keys(event.body.body.payments || {})[0];
        const payment = event.body.body.payments?.[paymentId];
        const method = payment?.paymentMethods?.[paymentId];
        const isBundleImmediate =
          event.body.metadata.internalAttributes?.IS_BUNDLE_IMMEDIATE ===
          "true";
        const isSeasonPassBundle =
          event.body.metadata.internalAttributes?.IS_SEASON_PASS_BUNDLE ===
          "true";
        const isPreorderBundle =
          event.body.metadata.internalAttributes?.IS_PREORDER_BUNDLE === "true";

        dbdOrderDetails = withOrderDetailsDefaults({
          orderDate: event.body.header.transactionDate,
          glProductGroup: firstProduct?.details.glProductLineId,
          consumptionType: firstProduct?.details.activity,
          fulfillmentType:
            event.body.metadata.internalAttributes?.FULFILLMENT_TYPE,
          lastItem: { digitalBusinessDocument: event },
          paymentMethod: method ? getDbdPaymentMethodName(method) : undefined,
          isBundleImmediate,
          isSeasonPassBundle,
          isPreorderBundle,
        });
      } catch (error) {
        // Malformed latest DBD should not erase older usable summary data.
        console.error("Failed to process DBD data:", error);
      }

      if (dbdOrderDetails) {
        return dbdOrderDetails;
      }
    }

    if (isDigitalCostDocument(event)) {
      return withOrderDetailsDefaults({
        orderDate: event.body.header.transactionDate,
        glProductGroup: event.body.body.glProductLineId,
        consumptionType: event.body.body.cost.transactionType,
        lastItem: { digitalCostDocument: event },
      });
    }

    if (isDigitalPaymentDocument(event)) {
      return withOrderDetailsDefaults({
        orderDate: event.body.body.glDate,
        glProductGroup: event.body.body.glProductLineId,
        lastItem: { digitalPaymentDocument: event },
        countryCodeOverride: event.body.body.offerTerritoryCountryCode,
      });
    }

    if (isAccountingEventsGroup(event)) {
      const revenueEvent = Object.values(event.accountingEvents).find(
        (accountingEvent) => accountingEvent.type === "REVENUE"
      );
      const fallbackEvent = Object.values(event.accountingEvents).find(
        (accountingEvent) => accountingEvent.type === "ROYALTY"
      );
      const selectedEvent = revenueEvent || fallbackEvent;
      let hideAccountingFlow = false;

      try {
        hideAccountingFlow = shouldHideAccountingFlow(eventData, cbeData);
      } catch (error) {
        console.error("Failed to determine accounting flow visibility:", error);
      }

      if (!hideAccountingFlow) {
        return withOrderDetailsDefaults({
          orderDate:
            selectedEvent?.orderDate || selectedEvent?.contractStartDate,
          glProductGroup: selectedEvent?.glProductGroup,
          consumptionType: revenueEvent?.consumptionType,
          lastItem: { accountingEventsGroup: event },
          paymentMethod: revenueEvent
            ? getPaymentMethod(revenueEvent)
            : undefined,
          declaredCountryCode: selectedEvent?.declaredCountryCode,
        });
      }
    }

    if (isCanonicalBusinessEvent(event)) {
      return withOrderDetailsDefaults({
        isMgm: event.vendorName === "MGM",
      });
    }
  }

  return withOrderDetailsDefaults({
    isBundleImmediate: false,
    isSeasonPassBundle: false,
    isPreorderBundle: false,
    isMgm: false,
  });
}
